import { FileAudio, Play, RefreshCw, Search, Square, Trash2, Upload, Volume2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteSound,
  loadSoundboard,
  playSound,
  refreshSoundOverlay,
  replaceSoundFile,
  stopAllSounds,
  updateSound,
  updateViewerSoundboardSettings,
  uploadSound,
  type SoundDefinition,
  type SoundboardSnapshot,
} from './sounds-api';
import './sounds.css';

type SoundDraft = {
  name: string;
  volume: string;
  cooldownSeconds: string;
  viewerName: string;
  viewerVolume: string;
  viewerCooldownSeconds: string;
  viewerSortOrder: string;
};

type ValidSoundDraft = {
  name: string;
  volume: number;
  cooldownSeconds: number;
  viewerName: string;
  viewerVolume: number;
  viewerCooldownSeconds: number;
  viewerSortOrder: number;
};

function draftFromSound(sound: SoundDefinition): SoundDraft {
  return {
    name: sound.name,
    volume: String(sound.volume),
    cooldownSeconds: String(sound.cooldownSeconds),
    viewerName: sound.viewerName,
    viewerVolume: String(sound.viewerVolume),
    viewerCooldownSeconds: String(sound.viewerCooldownSeconds),
    viewerSortOrder: String(sound.viewerSortOrder),
  };
}

function boundedNumber(value: string, minimum: number, maximum: number): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum ? parsed : null;
}

function boundedInteger(value: string, minimum: number, maximum: number): number | null {
  if (!/^-?\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= minimum && parsed <= maximum ? parsed : null;
}

function validSoundDraft(draft: SoundDraft | null): ValidSoundDraft | null {
  if (!draft?.name.trim() || !draft.viewerName.trim()) return null;
  const volume = boundedNumber(draft.volume, 0, 1);
  const cooldownSeconds = boundedInteger(draft.cooldownSeconds, 0, 86400);
  const viewerVolume = boundedNumber(draft.viewerVolume, 0, 1);
  const viewerCooldownSeconds = boundedInteger(draft.viewerCooldownSeconds, 0, 86400);
  const viewerSortOrder = boundedInteger(draft.viewerSortOrder, -1000000, 1000000);
  if (volume === null || cooldownSeconds === null || viewerVolume === null || viewerCooldownSeconds === null || viewerSortOrder === null) return null;
  return {
    name: draft.name.trim(),
    volume,
    cooldownSeconds,
    viewerName: draft.viewerName.trim(),
    viewerVolume,
    viewerCooldownSeconds,
    viewerSortOrder,
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatWhen(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB') : '—';
}

export function SoundsPage() {
  const [snapshot, setSnapshot] = useState<SoundboardSnapshot | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'enabled' | 'disabled' | 'viewer'>('all');
  const [sort, setSort] = useState<'name' | 'recent' | 'viewer-order'>('name');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<SoundDraft | null>(null);
  const [viewerStatusMessage, setViewerStatusMessage] = useState('');
  const [viewerGlobalCooldown, setViewerGlobalCooldown] = useState('2');
  const [viewerUserCooldown, setViewerUserCooldown] = useState('15');
  const [viewerMaxQueue, setViewerMaxQueue] = useState('5');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const viewerDraftDirty = useRef(false);

  const refresh = useCallback(async (resetViewerDraft = false) => {
    try {
      const next = await loadSoundboard();
      setSnapshot(next);
      setSelectedId((current) => current && next.sounds.some((sound) => sound.id === current) ? current : next.sounds[0]?.id ?? null);
      if (resetViewerDraft || !viewerDraftDirty.current) {
        setViewerStatusMessage(next.viewer.statusMessage);
        setViewerGlobalCooldown(String(next.viewer.globalCooldownSeconds));
        setViewerUserCooldown(String(next.viewer.viewerCooldownSeconds));
        setViewerMaxQueue(String(next.viewer.maxQueueLength));
        viewerDraftDirty.current = false;
      }
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Sound library could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(true);
  }, [refresh]);

  const selected = snapshot?.sounds.find((sound) => sound.id === selectedId) ?? null;

  useEffect(() => {
    if (selected) setDraft(draftFromSound(selected));
    else setDraft(null);
  }, [selected?.id, selected?.updatedAt]);

  const visibleSounds = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('en-GB');
    return [...(snapshot?.sounds ?? [])]
      .filter((sound) => {
        if (filter === 'enabled' && !sound.enabled) return false;
        if (filter === 'disabled' && sound.enabled) return false;
        if (filter === 'viewer' && !sound.viewerEnabled) return false;
        return !query || `${sound.name} ${sound.viewerName} ${sound.originalFilename}`.toLocaleLowerCase('en-GB').includes(query);
      })
      .sort((left, right) => {
        if (sort === 'recent') {
          const leftTime = left.lastTriggeredAt ? Date.parse(left.lastTriggeredAt) : 0;
          const rightTime = right.lastTriggeredAt ? Date.parse(right.lastTriggeredAt) : 0;
          if (leftTime !== rightTime) return rightTime - leftTime;
        } else if (sort === 'viewer-order') {
          if (left.viewerSortOrder !== right.viewerSortOrder) return left.viewerSortOrder - right.viewerSortOrder;
        }
        return left.name.localeCompare(right.name, 'en-GB', { numeric: true });
      });
  }, [filter, search, snapshot, sort]);

  async function mutate(operation: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      await operation();
      await refresh(false);
      setMessage(success);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Sound action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function uploadFiles(files: FileList | null) {
    if (!files?.length) return;
    setUploading(true);
    setMessage('');
    let completed = 0;
    try {
      for (const file of Array.from(files)) {
        setMessage(`Uploading ${completed + 1} of ${files.length}: ${file.name}`);
        await uploadSound(file);
        completed += 1;
      }
      await refresh(false);
      setMessage(`${completed.toLocaleString('en-GB')} sound${completed === 1 ? '' : 's'} uploaded.`);
    } catch (error) {
      await refresh(false);
      setMessage(`${completed.toLocaleString('en-GB')} uploaded before the failure. ${error instanceof Error ? error.message : 'Upload failed.'}`);
    } finally {
      setUploading(false);
    }
  }

  async function saveSelected() {
    if (!selected || !draft) return;
    const values = validSoundDraft(draft);
    if (!values) {
      setMessage('Check the sound settings: volumes must be 0–1, cooldowns 0–86,400 whole seconds, and viewer order between −1,000,000 and 1,000,000.');
      return;
    }
    await mutate(() => updateSound(selected.id, values), `${values.name} saved.`);
  }

  async function saveViewerSettings() {
    const globalCooldownSeconds = boundedInteger(viewerGlobalCooldown, 0, 3600);
    const viewerCooldownSeconds = boundedInteger(viewerUserCooldown, 0, 86400);
    const maxQueueLength = boundedInteger(viewerMaxQueue, 1, 100);
    if (globalCooldownSeconds === null || viewerCooldownSeconds === null || maxQueueLength === null || !viewerStatusMessage.trim()) {
      setMessage('Check viewer settings: global cooldown 0–3,600s, per-viewer cooldown 0–86,400s, queue 1–100, and a status message are required.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      await updateViewerSoundboardSettings({
        globalCooldownSeconds,
        viewerCooldownSeconds,
        maxQueueLength,
        statusMessage: viewerStatusMessage.trim(),
      });
      viewerDraftDirty.current = false;
      await refresh(true);
      setMessage('Viewer soundboard settings saved.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Viewer soundboard settings could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  async function replaceFile(file: File | undefined) {
    if (!selected || !file) return;
    await mutate(() => replaceSoundFile(selected.id, file), `${selected.name} audio file replaced.`);
  }

  if (loading && !snapshot) return <div className="ffs-next-loading">Loading Sounds…</div>;

  const soundValues = validSoundDraft(draft);
  const globalCooldownValue = boundedInteger(viewerGlobalCooldown, 0, 3600);
  const viewerCooldownValue = boundedInteger(viewerUserCooldown, 0, 86400);
  const maxQueueValue = boundedInteger(viewerMaxQueue, 1, 100);
  const viewerSettingsValid = globalCooldownValue !== null && viewerCooldownValue !== null && maxQueueValue !== null && Boolean(viewerStatusMessage.trim());
  const markViewerDraftDirty = () => { viewerDraftDirty.current = true; };

  return (
    <div className="ffs-workspace-page ffs-sounds-page">
      {message ? <div className="ffs-workspace-message">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div><small>AUDIO LIBRARY</small><h2>Sounds</h2><p>One catalogue for host playback and viewer-panel exposure, with the active queue and OBS audio path alongside it.</p></div>
        <div className="ffs-sound-hero-actions">
          <button type="button" className="ffs-button secondary" disabled={busy || uploading} onClick={() => void refresh(true)}><RefreshCw size={15} /> Refresh</button>
        </div>
      </section>

      <section className="ffs-workspace-card ffs-sound-upload-card">
        <div>
          <small>ADD TO LIBRARY</small>
          <h3>Upload sound files</h3>
          <p>MP3, WAV, OGG, M4A, AAC or WebM audio · up to 25 MB per file · select several files at once.</p>
        </div>
        <label className={`ffs-button primary ${uploading ? 'disabled' : ''}`}><Upload size={15} /> {uploading ? 'Uploading…' : 'Upload Sound(s)'}<input type="file" accept="audio/*" multiple disabled={uploading || busy} onChange={(event) => { void uploadFiles(event.target.files); event.currentTarget.value = ''; }} /></label>
      </section>

      <section className="ffs-sound-status-grid">
        <div className={snapshot?.production.agentConnected ? 'good' : 'bad'}><span>Stream PC Agent</span><strong>{snapshot?.production.agentConnected ? 'CONNECTED' : 'OFFLINE'}</strong><small>{snapshot?.production.agentName ?? 'No active production Agent'}</small></div>
        <div className={snapshot?.production.overlayStatus === 'online' ? 'good' : 'warning'}><span>Audio overlay</span><strong>{snapshot?.production.overlayStatus?.toUpperCase() ?? 'UNKNOWN'}</strong><small>{snapshot?.production.overlayClients ?? 0} browser-source client{snapshot?.production.overlayClients === 1 ? '' : 's'}</small></div>
        <div className={snapshot?.production.obsProvisioned ? 'good' : 'warning'}><span>OBS audio source</span><strong>{snapshot?.production.obsProvisioned ? 'PROVISIONED' : 'CHECK'}</strong><small>{snapshot?.production.localUrl ?? 'No local overlay URL'}</small></div>
        <div className={snapshot?.viewer.extensionEnabled ? 'good' : 'warning'}><span>Viewer panel</span><strong>{snapshot?.viewer.extensionEnabled ? 'ENABLED' : 'DISABLED'}</strong><small>{snapshot?.sounds.filter((sound) => sound.viewerEnabled).length.toLocaleString('en-GB')} exposed sounds</small></div>
      </section>

      <section className="ffs-workspace-card ffs-sound-queue-card">
        <header><div><small>PLAYBACK</small><h3>Queue &amp; active audio</h3></div><div className="ffs-inline-actions"><button type="button" className="ffs-button secondary" disabled={busy || !snapshot?.production.agentConnected} onClick={() => void mutate(() => refreshSoundOverlay(), 'OBS audio source refresh requested.')}><RefreshCw size={13} /> Refresh OBS Audio</button><button type="button" className="ffs-button danger" disabled={busy || (!snapshot?.active && !(snapshot?.queue.some((item) => item.status === 'queued' || item.status === 'dispatching')))} onClick={() => void mutate(() => stopAllSounds(), 'Active and queued sound playback stopped/cleared.')}><Square size={13} /> Stop / Clear Audio</button></div></header>
        <div className="ffs-sound-active-row"><div><span>Now playing</span><strong>{snapshot?.active?.soundName ?? 'Nothing'}</strong><small>{snapshot?.active ? `Started ${formatWhen(snapshot.active.startedAt)}` : 'Soundboard queue is idle.'}</small></div><div><span>Pending</span><strong>{(snapshot?.queue.filter((item) => item.status === 'queued' || item.status === 'dispatching').length ?? 0).toLocaleString('en-GB')}</strong></div></div>
        <div className="ffs-sound-queue-list">{snapshot?.queue.slice(0, 8).map((item) => <div key={item.id} className={item.status === 'failed' ? 'failed' : ''}><strong>{item.soundName}</strong><span>{item.status}</span><small>{formatWhen(item.requestedAt)}{item.errorMessage ? ` · ${item.errorMessage}` : ''}</small></div>)}</div>
      </section>

      <section className="ffs-sound-library-grid">
        <article className="ffs-workspace-card ffs-sound-library-card">
          <header><div><small>CATALOGUE</small><h3>Sound library</h3></div><strong>{visibleSounds.length.toLocaleString('en-GB')}</strong></header>
          <div className="ffs-sound-library-toolbar">
            <label className="ffs-search-field"><Search size={15} /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, viewer label or filename" /></label>
            <div>{([['all', 'All'], ['enabled', 'Enabled'], ['disabled', 'Disabled'], ['viewer', 'Viewer Panel']] as const).map(([value, label]) => <button type="button" key={value} className={filter === value ? 'selected' : ''} onClick={() => setFilter(value)}>{label}</button>)}</div>
            <label className="sort"><span>Sort</span><select value={sort} onChange={(event) => setSort(event.target.value as typeof sort)}><option value="name">Name A–Z</option><option value="recent">Recently played</option><option value="viewer-order">Viewer order</option></select></label>
          </div>
          <div className="ffs-sound-table">
            <div className="head"><span>Sound</span><span>Host</span><span>Viewer</span><span>Volume</span><span>Cooldown</span><span></span></div>
            {visibleSounds.map((sound) => <div className={`row ${selectedId === sound.id ? 'selected' : ''}`} key={sound.id} onClick={() => setSelectedId(sound.id)}>
              <button type="button" className="name" onClick={() => setSelectedId(sound.id)}><strong>{sound.name}</strong><small>{formatBytes(sound.sizeBytes)} · {sound.originalFilename}</small></button>
              <button type="button" className={`state ${sound.enabled ? 'on' : ''}`} disabled={busy} onClick={(event) => { event.stopPropagation(); void mutate(() => updateSound(sound.id, { enabled: !sound.enabled }), `${sound.name} ${sound.enabled ? 'disabled' : 'enabled'}.`); }}>{sound.enabled ? 'ON' : 'OFF'}</button>
              <button type="button" className={`state ${sound.viewerEnabled ? 'on' : ''}`} disabled={busy} onClick={(event) => { event.stopPropagation(); void mutate(() => updateSound(sound.id, { viewerEnabled: !sound.viewerEnabled }), `${sound.name} viewer-panel exposure ${sound.viewerEnabled ? 'disabled' : 'enabled'}.`); }}>{sound.viewerEnabled ? 'ON' : 'OFF'}</button>
              <span>{Math.round(sound.volume * 100)}%</span><span>{sound.cooldownSeconds}s{sound.cooldownRemainingSeconds ? ` · ${sound.cooldownRemainingSeconds}s left` : ''}</span>
              <button type="button" className="play" disabled={busy || !sound.enabled || sound.cooldownRemainingSeconds > 0} onClick={(event) => { event.stopPropagation(); void mutate(() => playSound(sound.id), `${sound.name} queued.`); }}><Play size={13} /> Play</button>
            </div>)}
            {visibleSounds.length === 0 ? <div className="ffs-empty-state"><FileAudio size={16} /> No sounds match this view.</div> : null}
          </div>
        </article>

        <article className="ffs-workspace-card ffs-sound-editor-card">
          {selected && draft ? <>
            <header><div><small>SELECTED SOUND</small><h3>{selected.name}</h3><p>{selected.mimeType} · {formatBytes(selected.sizeBytes)} · last played {formatWhen(selected.lastTriggeredAt)}</p></div><Volume2 size={18} /></header>
            <div className="ffs-sound-editor-fields">
              <label><span>Library name</span><input maxLength={120} value={draft.name} aria-invalid={!draft.name.trim()} onChange={(event) => setDraft((current) => current ? { ...current, name: event.target.value } : current)} /></label>
              <div className="two"><label><span>Host volume <small>0–1</small></span><input type="number" min="0" max="1" step="0.05" value={draft.volume} aria-invalid={boundedNumber(draft.volume, 0, 1) === null} onChange={(event) => setDraft((current) => current ? { ...current, volume: event.target.value } : current)} /></label><label><span>Host cooldown <small>seconds</small></span><input type="number" min="0" max="86400" value={draft.cooldownSeconds} aria-invalid={boundedInteger(draft.cooldownSeconds, 0, 86400) === null} onChange={(event) => setDraft((current) => current ? { ...current, cooldownSeconds: event.target.value } : current)} /></label></div>
              <div className="viewer-block"><strong>Viewer-panel attributes</strong><label><span>Viewer label</span><input maxLength={120} value={draft.viewerName} aria-invalid={!draft.viewerName.trim()} onChange={(event) => setDraft((current) => current ? { ...current, viewerName: event.target.value } : current)} /></label><div className="three"><label><span>Viewer volume</span><input type="number" min="0" max="1" step="0.05" value={draft.viewerVolume} aria-invalid={boundedNumber(draft.viewerVolume, 0, 1) === null} onChange={(event) => setDraft((current) => current ? { ...current, viewerVolume: event.target.value } : current)} /></label><label><span>Viewer cooldown</span><input type="number" min="0" max="86400" value={draft.viewerCooldownSeconds} aria-invalid={boundedInteger(draft.viewerCooldownSeconds, 0, 86400) === null} onChange={(event) => setDraft((current) => current ? { ...current, viewerCooldownSeconds: event.target.value } : current)} /></label><label><span>Viewer order</span><input type="number" min="-1000000" max="1000000" value={draft.viewerSortOrder} aria-invalid={boundedInteger(draft.viewerSortOrder, -1000000, 1000000) === null} onChange={(event) => setDraft((current) => current ? { ...current, viewerSortOrder: event.target.value } : current)} /></label></div></div>
              <div className="ffs-inline-actions"><button type="button" className="ffs-button primary" disabled={busy || soundValues === null} onClick={() => void saveSelected()}>Save Sound</button><label className="ffs-button secondary">Replace Audio<input type="file" accept="audio/*" disabled={busy} onChange={(event) => { void replaceFile(event.target.files?.[0]); event.currentTarget.value = ''; }} /></label><button type="button" className="ffs-button danger" disabled={busy} onClick={() => { if (window.confirm(`Delete ${selected.name} from the sound library?`)) void mutate(() => deleteSound(selected.id), `${selected.name} deleted.`); }}><Trash2 size={13} /> Delete</button></div>
            </div>
          </> : <div className="ffs-empty-state">Select a sound to edit its host and viewer settings.</div>}
        </article>
      </section>

      <section className="ffs-workspace-card ffs-viewer-sound-settings">
        <header><div><small>TWITCH VIEWER PANEL</small><h3>Viewer soundboard settings</h3></div><span>{snapshot?.viewer.configuredChannelId ? `Channel ${snapshot.viewer.configuredChannelId}` : 'Channel follows Twitch authorisation'}</span></header>
        <div className="ffs-viewer-setting-switches"><button type="button" className={`ffs-sound-setting-switch ${snapshot?.viewer.extensionEnabled ? 'on' : ''}`} disabled={busy || !snapshot} onClick={() => void mutate(() => updateViewerSoundboardSettings({ extensionEnabled: !snapshot?.viewer.extensionEnabled }), `Viewer soundboard ${snapshot?.viewer.extensionEnabled ? 'disabled' : 'enabled'}.`)}><strong>Viewer panel</strong><span>{snapshot?.viewer.extensionEnabled ? 'Enabled' : 'Disabled'}</span></button><button type="button" className={`ffs-sound-setting-switch ${snapshot?.viewer.publicFallbackEnabled ? 'on' : ''}`} disabled={busy || !snapshot} onClick={() => void mutate(() => updateViewerSoundboardSettings({ publicFallbackEnabled: !snapshot?.viewer.publicFallbackEnabled }), `Public fallback ${snapshot?.viewer.publicFallbackEnabled ? 'disabled' : 'enabled'}.`)}><strong>Public fallback</strong><span>{snapshot?.viewer.publicFallbackEnabled ? 'Enabled' : 'Disabled'}</span></button></div>
        <div className="ffs-viewer-setting-fields"><label><span>Global cooldown</span><div><input type="number" min="0" max="3600" value={viewerGlobalCooldown} aria-invalid={globalCooldownValue === null} onChange={(event) => { markViewerDraftDirty(); setViewerGlobalCooldown(event.target.value); }} /><b>seconds</b></div></label><label><span>Per-viewer cooldown</span><div><input type="number" min="0" max="86400" value={viewerUserCooldown} aria-invalid={viewerCooldownValue === null} onChange={(event) => { markViewerDraftDirty(); setViewerUserCooldown(event.target.value); }} /><b>seconds</b></div></label><label><span>Maximum queue</span><input type="number" min="1" max="100" value={viewerMaxQueue} aria-invalid={maxQueueValue === null} onChange={(event) => { markViewerDraftDirty(); setViewerMaxQueue(event.target.value); }} /></label><label className="message"><span>Panel status message</span><input maxLength={240} value={viewerStatusMessage} aria-invalid={!viewerStatusMessage.trim()} onChange={(event) => { markViewerDraftDirty(); setViewerStatusMessage(event.target.value); }} /></label><button type="button" className="ffs-button primary" disabled={busy || !viewerSettingsValid} onClick={() => void saveViewerSettings()}>Save Viewer Settings</button></div>
      </section>
    </div>
  );
}